import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, FormField, InputNumber, TextFormLabel, TextHelper, Tooltip } from '@allygpo/ally-ux';

// Bipolar "size the category up/down" control. Works internally in integer
// percentage delta vs. last quarter (0% = baseline = 100% of last quarter).
// The bounds map to a scale factor (1 + delta/100) that stays within the
// backend-accepted band (gt 0, le 5.0).
const MIN = -95;
const MAX = 100;
const RANGE = MAX - MIN; // 195
const BASE_PCT = ((0 - MIN) / RANGE) * 100; // 48.72% — where 0% sits on the track

const DEFAULT_FACTOR = 1.0;

interface CategorySizeControlProps {
  /** Category this control applies to (used for labelling). */
  category: string;
  /** Current scale factor (1.0 = 100% of last quarter). */
  factor: number;
  /** Called with the new scale factor whenever the user changes the size. */
  onChange: (factor: number) => void;
  /**
   * Last quarter's actual category units for this category (the "Current" total).
   * When provided, the helper line shows the modeled unit count instead of a bare %.
   */
  baselineUnits?: number | null;
  /** Shown after "Category size" when several areas are in the run. */
  showCategoryName?: boolean;
  disabled?: boolean;
}

const clampDelta = (n: number): number => Math.max(MIN, Math.min(MAX, Math.round(n)));

// Keep the emitted factor within the backend-accepted band (gt 0, le 5.0).
const clampFactor = (factor: number): number => Math.min(5, Math.max(0.05, factor));

export const factorToDelta = (factor: number | undefined): number =>
  clampDelta(Math.round(((factor ?? DEFAULT_FACTOR) - 1) * 100));

const deltaToFactor = (delta: number): number =>
  clampFactor(1 + clampDelta(delta) / 100);

const fmt = (v: number): string => `${v > 0 ? '+' : ''}${v}%`;

/** "3,298 category units · today's baseline" / "3,628 category units · baseline 3,298". */
export const categoryUnitsLine = (baselineUnits: number | null | undefined, factor: number | undefined): string => {
  const delta = factorToDelta(factor);
  if (baselineUnits == null) {
    return delta === 0 ? "Last quarter's category units · today's baseline" : `${fmt(delta)} of last quarter's category units`;
  }
  const modeled = Math.round(baselineUnits * (1 + delta / 100));
  return delta === 0
    ? `${baselineUnits.toLocaleString('en-US')} category units · today's baseline`
    : `${modeled.toLocaleString('en-US')} category units · baseline ${baselineUnits.toLocaleString('en-US')}`;
};

/**
 * Per-category control that lets the user model a larger or smaller category than
 * last quarter's actual buy. Emits a scale factor (1.0 = no change) consumed by the
 * scenario generation request. Presentational only — all state lives in the parent.
 */
const CategorySizeControl: React.FC<CategorySizeControlProps> = ({
  category,
  factor,
  onChange,
  baselineUnits = null,
  showCategoryName = false,
  disabled = false,
}) => {
  const committed = useMemo(() => factorToDelta(factor), [factor]);
  // While dragging, the value lives here and only this control re-renders; the parent gets it on release.
  // Committing on every pointer move re-rendered the whole New run page and made the handle stick.
  const [dragValue, setDragValue] = useState<number | null>(null);
  const value = dragValue ?? committed;
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const commit = useCallback(
    (v: number) => {
      if (disabled) return;
      onChange(deltaToFactor(v));
    },
    [disabled, onChange]
  );

  // derived geometry
  const pct = ((value - MIN) / RANGE) * 100; // handle position, %
  const fillLeft = value < 0 ? pct : BASE_PCT;
  const fillWidth = Math.abs(pct - BASE_PCT);

  // drag handling
  const posToValue = (clientX: number): number => {
    const el = trackRef.current;
    if (!el) return value;
    const r = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return MIN + ratio * RANGE;
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || e.button !== 0) return;
    // No text selection or native drag: either cancels the pointer stream mid-drag.
    e.preventDefault();
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.currentTarget.querySelector<HTMLElement>('[role="slider"]')?.focus();
    setDragValue(clampDelta(posToValue(e.clientX)));
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const next = clampDelta(posToValue(e.clientX));
    setDragValue((prev) => (prev === next ? prev : next));
  };
  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (dragValue !== null && dragValue !== committed) commit(dragValue);
    setDragValue(null);
  };

  const slug = category.replace(/[^a-zA-Z0-9]/g, '');
  const infoClass = `category-size-info-${slug}`;
  const resetClass = `category-size-reset-${slug}`;
  const inputId = `category-size-input-${slug}`;

  return (
    <FormField className={`category-size min-w-0${disabled ? ' is-disabled' : ''}`}>
      <Tooltip target={`.${infoClass}`} position="top" />
      <Tooltip target={`.${resetClass}`} position="top" />

      <div className="flex align-items-center justify-content-between u-gap-2">
        <TextFormLabel className="inline-flex align-items-center u-gap-1" htmlFor={inputId}>
          Category size{showCategoryName ? ` · ${category}` : ''}
          <i
            className={`pi pi-info-circle category-size-info text-tertiary text-2xs ${infoClass}`}
            data-pr-tooltip="Model this category at a larger or smaller volume than last quarter's actual category units. 0% = same as last quarter's actual units."
            data-pr-position="top"
          />
        </TextFormLabel>
        {value !== 0 && (
          <Button
            variant="link"
            className={`text-2xs ${resetClass}`}
            data-pr-tooltip="Reset to baseline"
            data-pr-position="top"
            onClick={() => commit(0)}
            disabled={disabled}
            aria-label={`Reset ${category} size to baseline`}
          >
            Reset
          </Button>
        )}
      </div>

      <div className="flex align-items-center u-gap-3">
        <span className="flex-none w-5rem">
          <InputNumber
            inputId={inputId}
            suffix="%"
            inputClassName="w-full text-right"
            value={value}
            onValueChange={(e) => commit(typeof e.value === 'number' ? e.value : 0)}
            min={MIN}
            max={MAX}
            step={1}
            useGrouping={false}
            minFractionDigits={0}
            maxFractionDigits={0}
            disabled={disabled}
            inputMode="numeric"
            aria-label={`Category size percent for ${category}`}
          />
        </span>
        <div
          ref={trackRef}
          className="category-size-track"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="category-size-rail" />
          <span className="category-size-fill" style={{ left: `${fillLeft}%`, width: `${fillWidth}%` }} />
          <span className="category-size-zero" style={{ left: `${BASE_PCT}%` }} />
          <span
            role="slider"
            className="category-size-handle"
            aria-label={`Category size for ${category}`}
            aria-valuemin={MIN}
            aria-valuemax={MAX}
            aria-valuenow={value}
            aria-valuetext={fmt(value)}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                e.preventDefault();
                commit(value + 1);
              }
              if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                e.preventDefault();
                commit(value - 1);
              }
            }}
            style={{ left: `${pct}%` }}
          />
          <span className="category-size-scale text-2xs text-tertiary" aria-hidden="true">
            <span className="is-min">{fmt(MIN)}</span>
            <span className="is-zero" style={{ left: `${BASE_PCT}%` }}>0%</span>
            <span className="is-max">{fmt(MAX)}</span>
          </span>
        </div>
      </div>

      <TextHelper>{categoryUnitsLine(baselineUnits, dragValue === null ? factor : deltaToFactor(dragValue))}</TextHelper>
    </FormField>
  );
};

export default CategorySizeControl;
